import type { ComponentProps } from 'react';

import { tv } from '../../lib/tv';

export type AspectRatio = '9:16' | '1:1' | '16:9' | '4:5';

export type AspectGlyphProps = Omit<ComponentProps<'svg'>, 'children'> & {
  /** Figma "Ratio". */
  ratio: AspectRatio;
};

/**
 * The frame per ratio inside the 40×40 glyph, as Figma draws it (x, y, w, h).
 * The 1.5px stroke is inside, so the path runs 0.75px in from each edge.
 */
const FRAMES: Record<AspectRatio, [x: number, y: number, w: number, h: number]> = {
  '9:16': [11, 4, 18, 32],
  '1:1': [6, 6, 28, 28],
  '16:9': [3, 10.5, 34, 19],
  '4:5': [8, 5, 24, 30],
};
const STROKE = 1.5;
const RADIUS = 4;

const glyph = tv({
  slots: {
    root: 'size-10 shrink-0',
    frame: 'stroke-brand-strong fill-(--aspect-glyph-fill,var(--color-brand-subtle))',
  },
});

/**
 * Proportional frame for aspect-ratio choices, 9:16 · 1:1 · 16:9 · 4:5
 * (Figma: Components ↳ Choice Card ↳ Aspect Glyph): a brand/subtle frame with
 * a 1.5px brand/strong inside stroke and radius 4 in a 40×40 box. Scales with
 * its size (`className="size-6"`). Decorative unless given an `aria-label`.
 * A selected Choice Card turns the fill bg/surface (`--aspect-glyph-fill`).
 */
export function AspectGlyph({ ratio, className, ...props }: AspectGlyphProps) {
  const [x, y, w, h] = FRAMES[ratio];
  const inset = STROKE / 2;
  const styles = glyph();
  const labelled = Boolean(props['aria-label'] || props['aria-labelledby']);
  return (
    <svg
      data-slot="aspect-glyph"
      data-ratio={ratio}
      viewBox="0 0 40 40"
      role={labelled ? 'img' : undefined}
      aria-hidden={labelled ? undefined : true}
      className={styles.root({ className })}
      {...props}
    >
      <rect
        data-slot="aspect-glyph-frame"
        x={x + inset}
        y={y + inset}
        width={w - STROKE}
        height={h - STROKE}
        rx={RADIUS - inset}
        strokeWidth={STROKE}
        className={styles.frame()}
      />
    </svg>
  );
}
